'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Select, createSelectListCollection } from '../../../components/select';
import { BodyStrong, MetaDefault } from '../../../components/typography';
import { AutomationDayToggleGroup } from '../../automation/AutomaticExecutionsConfiguration';
import { AutomationToggleRow } from '../../automation/AutomationToggleRow';
import {
  buildEndHourSelectItems,
  buildStartHourSelectItems,
  buildTimezoneSelectItems,
  DEFAULT_DAYS_AUTOMATIC_EXECUTIONS,
  formatScheduleTimeRangeLabel,
  getBrowserTimezone,
  isValidScheduleHourRange,
  resolveScheduleTimezoneSelection,
  WORKING_HOURS_MAX,
  WORKING_HOURS_MIN,
  type AutomationSchedule,
} from '../../automation/automationScheduleShared';
import { SettingsCard } from '../SettingsCard';
import { SettingsEditDialog } from '../SettingsEditDialog';
import { SettingsRow } from '../SettingsRow';

export type ScanningConfigurationChange = {
  smokeTestEnabled?: boolean;
  limitScheduleEnabled?: boolean;
  automationSchedule?: AutomationSchedule;
};

export type ScanningConfigurationCardProps = {
  smokeTestEnabled: boolean;
  limitScheduleEnabled: boolean;
  automationSchedule: AutomationSchedule;
  disabled?: boolean;
  isUpdating?: boolean;
  onChange: (change: ScanningConfigurationChange) => void;
  scheduleOnlyMode?: boolean;
  limitScheduleLabel: string;
  limitScheduleTooltip: string;
  scheduleDetailsTitle?: string;
  runAutomatedLabel?: string;
  runAutomatedTooltip?: string;
};

export const DEFAULT_SCANNING_SCHEDULE: AutomationSchedule = {
  daysOfWeek: [...DEFAULT_DAYS_AUTOMATIC_EXECUTIONS],
  startHour: WORKING_HOURS_MIN,
  endHour: WORKING_HOURS_MAX,
  timezone: getBrowserTimezone(),
};

export function ScanningConfigurationCard({
  smokeTestEnabled,
  limitScheduleEnabled,
  automationSchedule,
  disabled,
  isUpdating,
  onChange,
  scheduleOnlyMode = false,
  limitScheduleLabel,
  limitScheduleTooltip,
  scheduleDetailsTitle,
  runAutomatedLabel,
  runAutomatedTooltip,
}: ScanningConfigurationCardProps) {
  const tExecutions = useTranslations('designSystem.automation.executions');
  const tSchedule = useTranslations('designSystem.automation.schedule');
  const tCommon = useTranslations('designSystem.common');
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [isTimeRangeDialogOpen, setIsTimeRangeDialogOpen] = useState(false);
  const [timezoneDraft, setTimezoneDraft] = useState<string | null>(null);
  const [startHourDraft, setStartHourDraft] = useState<number | null>(null);
  const [endHourDraft, setEndHourDraft] = useState<number | null>(null);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const selectedTimezone = resolveScheduleTimezoneSelection({
    scheduleTimezone: automationSchedule.timezone,
    browserTimezone,
  });
  const timezoneItems = buildTimezoneSelectItems({
    browserTimezone,
    selectedTimezone,
  });
  const timezoneCollection = createSelectListCollection({
    items: timezoneItems,
  });
  const selectedTimezoneLabel =
    timezoneItems.find((item) => item.value === selectedTimezone)?.label ??
    selectedTimezone;
  const safeStartHourDraft = startHourDraft ?? automationSchedule.startHour;
  const safeEndHourDraft = endHourDraft ?? automationSchedule.endHour;
  const selectedDays = automationSchedule.daysOfWeek;
  const startHourDraftItems = useMemo(
    () => buildStartHourSelectItems(safeEndHourDraft),
    [safeEndHourDraft],
  );
  const endHourDraftItems = useMemo(
    () => buildEndHourSelectItems(safeStartHourDraft),
    [safeStartHourDraft],
  );
  const startHourCollection = useMemo(
    () => createSelectListCollection({ items: startHourDraftItems }),
    [startHourDraftItems],
  );
  const endHourCollection = useMemo(
    () => createSelectListCollection({ items: endHourDraftItems }),
    [endHourDraftItems],
  );
  const timeRangeValue = formatScheduleTimeRangeLabel({
    startHour: automationSchedule.startHour,
    endHour: automationSchedule.endHour,
    toLabel: tSchedule('timeRangeTo'),
    nextDayLabel: tSchedule('timeRangeNextDay'),
  });
  const resolvedRunAutomatedLabel =
    runAutomatedLabel ?? tExecutions('runAutomated.label');
  const resolvedRunAutomatedTooltip =
    runAutomatedTooltip ?? tExecutions('runAutomated.tooltip');
  const resolvedScheduleDetailsTitle =
    scheduleDetailsTitle ?? tExecutions('timeLimitation.title');
  const showScheduleControls = scheduleOnlyMode
    ? limitScheduleEnabled
    : smokeTestEnabled && limitScheduleEnabled;
  const handleDaysChange = (day: number) => {
    const isSelected = automationSchedule.daysOfWeek.includes(day);

    onChange({
      automationSchedule: {
        ...automationSchedule,
        daysOfWeek: isSelected
          ? automationSchedule.daysOfWeek.filter((d) => d !== day)
          : [...automationSchedule.daysOfWeek, day],
      },
    });
  };

  return (
    <VStack gap={6} alignItems="stretch" w="full">
      <SettingsCard>
        <VStack gap={3} alignItems="stretch">
          {!scheduleOnlyMode ? (
            <AutomationToggleRow
              label={resolvedRunAutomatedLabel}
              tooltipTitle={resolvedRunAutomatedTooltip}
              checked={smokeTestEnabled}
              yesLabel={tExecutions('yes')}
              noLabel={tExecutions('no')}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onChange({ smokeTestEnabled: checked })
              }
            />
          ) : null}

          {(scheduleOnlyMode || smokeTestEnabled) && (
            <>
              {!scheduleOnlyMode ? <Box h="1px" bg="border.default" /> : null}
              <AutomationToggleRow
                label={limitScheduleLabel}
                tooltipTitle={limitScheduleTooltip}
                checked={limitScheduleEnabled}
                yesLabel={tExecutions('yes')}
                noLabel={tExecutions('no')}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  onChange({
                    limitScheduleEnabled: checked,
                    automationSchedule: checked
                      ? automationSchedule
                      : DEFAULT_SCANNING_SCHEDULE,
                  });
                }}
              />
            </>
          )}
        </VStack>
      </SettingsCard>

      {showScheduleControls && (
        <VStack gap={3} alignItems="stretch">
          <BodyStrong>{resolvedScheduleDetailsTitle}</BodyStrong>
          <SettingsCard>
            <SettingsRow
              label={tExecutions('daysOfWeek.label')}
              tooltip={tExecutions('daysOfWeek.tooltip')}
              control={
                <AutomationDayToggleGroup
                  daysOfWeek={selectedDays}
                  disabled={disabled || isUpdating}
                  onToggleDay={handleDaysChange}
                />
              }
            />
            <SettingsRow
              label={tSchedule('timezone')}
              value={selectedTimezoneLabel}
              action={{
                label: tCommon('edit'),
                onClick: () => {
                  setTimezoneDraft(selectedTimezone);
                  setIsTimezoneDialogOpen(true);
                },
                disabled: isUpdating || disabled,
              }}
              isReadOnly={disabled}
            />
            <SettingsRow
              label={tSchedule('timeRange')}
              value={timeRangeValue}
              action={{
                label: tCommon('edit'),
                onClick: () => {
                  setStartHourDraft(automationSchedule.startHour);
                  setEndHourDraft(automationSchedule.endHour);
                  setIsTimeRangeDialogOpen(true);
                },
                disabled: isUpdating || disabled,
              }}
              isReadOnly={disabled}
            />
          </SettingsCard>
        </VStack>
      )}

      <SettingsEditDialog
        open={isTimezoneDialogOpen}
        title={tSchedule('timezone')}
        onOpenChange={setIsTimezoneDialogOpen}
        onSubmit={() => {
          if (timezoneDraft) {
            onChange({
              automationSchedule: {
                ...automationSchedule,
                timezone: timezoneDraft,
              },
            });
          }

          setIsTimezoneDialogOpen(false);
        }}
      >
        <Box w="full">
          <Select.Root
            collection={timezoneCollection}
            positioning={{ sameWidth: true }}
            value={[timezoneDraft ?? selectedTimezone]}
            onValueChange={({ value }) => {
              const v = value[0];

              if (v) setTimezoneDraft(v);
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={tSchedule('selectTimezone')} />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {timezoneItems.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>

      <SettingsEditDialog
        open={isTimeRangeDialogOpen}
        title={tSchedule('timeRange')}
        onOpenChange={setIsTimeRangeDialogOpen}
        onSubmit={() => {
          onChange({
            automationSchedule: {
              ...automationSchedule,
              startHour: safeStartHourDraft,
              endHour: safeEndHourDraft,
            },
          });
          setIsTimeRangeDialogOpen(false);
        }}
        isSubmitDisabled={
          !isValidScheduleHourRange({
            startHour: safeStartHourDraft,
            endHour: safeEndHourDraft,
          })
        }
      >
        <HStack gap={2} alignItems="center" w="full">
          <Box flex={1}>
            <Select.Root
              collection={startHourCollection}
              positioning={{ sameWidth: true }}
              value={[String(safeStartHourDraft)]}
              onValueChange={({ value }) => {
                const n = Number.parseInt(value[0] ?? '', 10);

                if (!Number.isNaN(n)) setStartHourDraft(n);
              }}
              size="sm"
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                  <ArrowDown01Icon size={14} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup maxH="240px" overflowY="auto">
                    {startHourDraftItems.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
          <MetaDefault color="fg.neutral.subtle">
            {tSchedule('timeRangeTo')}
          </MetaDefault>
          <Box flex={1}>
            <Select.Root
              collection={endHourCollection}
              positioning={{ sameWidth: true }}
              value={[String(safeEndHourDraft)]}
              onValueChange={({ value }) => {
                const n = Number.parseInt(value[0] ?? '', 10);

                if (!Number.isNaN(n)) setEndHourDraft(n);
              }}
              size="sm"
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                  <ArrowDown01Icon size={14} />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup maxH="240px" overflowY="auto">
                    {endHourDraftItems.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon size={16} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Box>
        </HStack>
      </SettingsEditDialog>
    </VStack>
  );
}
